import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import Logo from "../components/Logo";
import { CheckCircle, XCircle, ArrowsClockwise, FilePdf } from "@phosphor-icons/react";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2000;

export default function CommanditesPaiement() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", attempt: 0, data: null, error: null });
  const polledRef = useRef(false);

  useEffect(() => {
    document.title = "Paiement commandite — Gala GDN";
    if (!sessionId) { setState({ phase: "error", error: "session_id manquant" }); return; }
    if (polledRef.current) return;
    polledRef.current = true;

    let cancelled = false;
    let attempt = 0;
    const tick = async () => {
      if (cancelled) return;
      attempt += 1;
      try {
        const r = await api.get(`/sponsorships/checkout/status/${sessionId}`);
        const d = r.data;
        if (d.payment_status === "paid") return setState({ phase: "success", attempt, data: d });
        if (d.status === "expired") return setState({ phase: "expired", attempt, data: d });
        setState(s => ({ ...s, attempt, data: d }));
        if (attempt >= MAX_ATTEMPTS) return setState({ phase: "timeout", attempt, data: d });
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ phase: "error", error: e?.response?.data?.detail || "Erreur" });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const receiptUrl = state.data?.order_id ? `${API}/sponsorships/receipt/${state.data.order_id}.pdf` : null;

  return (
    <div className="min-h-screen bg-background" data-testid="commandites-paiement-page">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 hard-border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <Link to="/commandites" className="ticker text-muted-foreground hover:text-foreground" data-testid="nav-back">← Retour</Link>
        </div>
      </nav>
      <div className="max-w-2xl mx-auto px-6 py-16">
        {state.phase === "polling" && (
          <div className="hard-border bg-white p-8 space-y-3" data-testid="poll-state">
            <div className="flex items-center gap-3">
              <ArrowsClockwise size={36} className="animate-spin text-[#D90429]" />
              <div>
                <div className="font-display font-black text-2xl">Vérification du paiement…</div>
                <div className="text-sm text-muted-foreground">Tentative {state.attempt}/{MAX_ATTEMPTS}</div>
              </div>
            </div>
          </div>
        )}
        {state.phase === "success" && (
          <div className="hard-border bg-white p-8 space-y-6" data-testid="success-state">
            <div className="flex items-center gap-3">
              <CheckCircle size={48} weight="fill" className="text-[#0A8754]" />
              <div>
                <div className="ticker text-muted-foreground">COMMANDITE CONFIRMÉE</div>
                <div className="font-display font-black text-3xl">{state.data?.order_no}</div>
              </div>
            </div>
            <div className="text-sm">Montant payé : <strong className="tabular-nums">{((state.data?.amount_total || 0) / 100).toFixed(2)} {(state.data?.currency || "cad").toUpperCase()}</strong></div>
            <div className="bg-[#FFF6E5] hard-border p-4 text-sm space-y-2">
              <div className="font-bold">📄 Reçu de commandite officiel</div>
              <div>Télécharge ton reçu PDF avec la ventilation TPS/TVQ pour ton comptable. Cette commandite est <strong>déductible 100 %</strong> comme dépense de publicité.</div>
              <a
                href={receiptUrl}
                download
                className="inline-flex items-center gap-2 bg-foreground text-white px-4 py-2 ticker hover:bg-black transition-colors mt-2"
                data-testid="download-receipt"
              >
                <FilePdf size={18} weight="bold" /> Télécharger le reçu PDF
              </a>
            </div>
            <Link to="/commandites" className="inline-block hard-border px-5 py-2.5 ticker hover:bg-muted transition-colors">Retour</Link>
          </div>
        )}
        {state.phase === "timeout" && (
          <div className="hard-border bg-white p-8 space-y-3" data-testid="timeout-state">
            <div className="font-display font-black text-2xl">Délai dépassé</div>
            <div className="text-sm">Le paiement n'est pas encore confirmé. Si ta carte a été débitée, contacte impactkb.bjj@gmail.com avec le n° {state.data?.order_no}.</div>
          </div>
        )}
        {state.phase === "expired" && (
          <div className="hard-border bg-white p-8 space-y-3">
            <div className="flex items-center gap-3"><XCircle size={36} weight="fill" className="text-[#D90429]" /><div className="font-display font-black text-2xl">Session expirée</div></div>
            <Link to="/commandites" className="inline-block bg-[#D90429] text-white px-5 py-2.5 ticker hover:bg-[#BF0424]">Recommencer</Link>
          </div>
        )}
        {state.phase === "error" && (
          <div className="hard-border bg-white p-8 space-y-3" data-testid="error-state">
            <div className="flex items-center gap-3"><XCircle size={36} weight="fill" className="text-[#D90429]" /><div className="font-display font-black text-2xl">Erreur</div></div>
            <div className="text-sm">{state.error}</div>
          </div>
        )}
      </div>
    </div>
  );
}
